import { ClientResponseError } from "pocketbase"

import { staffTenants } from "@/lib/api/auth"
import { fetchSupportShop, supportAccess } from "@/lib/api/support"
import { getIdToken } from "@/lib/firebase"
import {
  bindTenantSession,
  mainPb,
  tenantAuthStore,
  tenantPb,
  useTenantServer,
} from "@/lib/pb/clients"
import type { TenantAccess } from "@/lib/api/types"

/**
 * The sign-in chain.
 *
 *   Firebase ID token
 *     -> MainApp   /api/firebase-auth   a session, and the list of businesses
 *     -> MainApp   /api/shard-token     a five-minute token for one business
 *     -> svrN      /api/session         a session on that business's server
 *
 * Each link is only re-minted when the one above it has expired, so a warm
 * load spends nothing and a cold one spends three requests.
 */

/** Nobody is signed in with Firebase, so none of the rest can be attempted. */
export class NotSignedInError extends Error {
  constructor() {
    super("Not signed in")
    this.name = "NotSignedInError"
  }
}

/**
 * The address this person signed in with is not confirmed, and it already
 * belongs to an account (MANIT-895). Until they confirm it, MainApp will
 * neither hand that account over nor make a second one; `/confirm-email`
 * walks them through it.
 */
export class ConfirmEmailError extends Error {
  constructor() {
    super("Confirm your email to continue.")
    this.name = "ConfirmEmailError"
  }
}

/** MainApp names this refusal by code, as the class-full one: `{ data: { code: { code } } }`. */
const CONFIRM_EMAIL_CODE = "confirm_email"

function asksToConfirmEmail(error: unknown): boolean {
  if (!(error instanceof ClientResponseError) || error.status !== 403) return false
  const entry = (error.response?.data as { code?: { code?: unknown } } | undefined)?.code
  return entry?.code === CONFIRM_EMAIL_CODE
}

interface FirebaseAuthResponse {
  token: string
  record: Record<string, unknown>
  // PocketBase puts anything an auth endpoint adds of its own under `meta`,
  // alongside the OAuth data it puts there itself — not at the top level.
  meta?: { tenants?: TenantAccess[]; support?: boolean }
}

interface ShardTokenResponse {
  token: string
  serverUrl: string
  tenantId: string
  role: string
}

/**
 * Exchanges the Firebase session for a MainApp one and reports the businesses
 * it grants.
 *
 * Always makes the call, because the tenant list is the other half of the
 * answer and a business the user was just invited to would otherwise not
 * appear until the token expired.
 */
export function authenticateMain(): Promise<TenantAccess[]> {
  // Several screens ask at once on a cold load. On a first sign-in, parallel
  // calls each tried to create the same account, and all but one failed.
  inFlight ??= exchangeFirebaseToken().finally(() => {
    inFlight = null
  })
  return inFlight
}

let inFlight: Promise<TenantAccess[]> | null = null

async function exchangeFirebaseToken(): Promise<TenantAccess[]> {
  const idToken = await getIdToken()
  if (!idToken) throw new NotSignedInError()

  let response: FirebaseAuthResponse
  try {
    response = await mainPb.send<FirebaseAuthResponse>("/api/firebase-auth", {
      method: "POST",
      body: { idToken },
    })
  } catch (error) {
    if (asksToConfirmEmail(error)) throw new ConfirmEmailError()
    throw error
  }

  mainPb.authStore.save(response.token, response.record as never)
  supportAccount = response.meta?.support === true

  return response.meta?.tenants ?? []
}

let supportAccount = false

/**
 * Whether the signed-in account is the support account, as MainApp said at
 * the last sign-in exchange. Only decides what the screens offer: every
 * support route checks again for itself.
 */
export function isSupportAccount(): boolean {
  return supportAccount
}

/** A MainApp session, minted only if there isn't one. */
export async function ensureMainSession(): Promise<void> {
  if (mainPb.authStore.isValid) return
  await authenticateMain()
}

/**
 * Opens a session on the server holding this business.
 *
 * The shard token is a five-minute bearer for one tenant: it is spent
 * immediately for a real session and never stored.
 */
export function openTenantSession(tenant: TenantAccess): Promise<void> {
  // The first dashboard load asks from several places at once. Each call used
  // to mint its own token and open its own session, so a first sign-in raced
  // itself on the server; one exchange per business serves them all.
  let pending = tenantInFlight.get(tenant.id)
  if (!pending) {
    pending = exchangeShardToken(tenant).finally(() => {
      tenantInFlight.delete(tenant.id)
    })
    tenantInFlight.set(tenant.id, pending)
  }
  return pending
}

const tenantInFlight = new Map<string, Promise<void>>()

async function exchangeShardToken(tenant: TenantAccess): Promise<void> {
  await ensureMainSession()

  // A support pass is asked for by name, so a shop the account also works
  // at is never opened in the hidden seat by accident, nor the reverse.
  const shard = await mainPb.send<ShardTokenResponse>("/api/shard-token", {
    method: "POST",
    body: { tenantId: tenant.id, support: tenant.support === true },
  })

  // The tenant record names its server, but the token is the authority on
  // where it may be spent.
  useTenantServer(shard.serverUrl)

  const session = await tenantPb.send<{
    token: string
    record: Record<string, unknown>
  }>("/api/session", { method: "POST", body: { token: shard.token } })

  // Filed under the business it was opened for, even if this tab has moved
  // on to another one meanwhile (MANIT-1163).
  tenantAuthStore.saveFor(tenant, session.token, session.record as never)
}

/**
 * Guarantees a usable session on the given business's server.
 *
 * An expired tenant token is repaired by walking back up the chain, not by
 * sending the user to the login screen: only a dead Firebase session is a real
 * sign-out, and that surfaces as NotSignedInError.
 */
export async function ensureTenantSession(tenant: TenantAccess): Promise<void> {
  useTenantServer(tenant.serverUrl)
  bindTenantSession(tenant)

  // A session opened under another role is one MainApp has since changed; the
  // shard ends those itself, and this catches the one this tab still holds.
  if (
    tenantPb.authStore.isValid &&
    tenantPb.authStore.record?.tenant_id === tenant.id &&
    tenantPb.authStore.record?.role === tenant.role
  ) {
    return
  }

  await openTenantSession(tenant)
}

/**
 * What the account may do in this business now, asked of MainApp rather than
 * read from what was saved when the business was picked — an admin may have
 * changed it since. Null when it may no longer work there at all.
 */
export async function currentTenantAccess(tenant: TenantAccess): Promise<TenantAccess | null> {
  const tenants = staffTenants(await authenticateMain())
  const member = tenants.find((candidate) => candidate.id === tenant.id) ?? null
  if (!tenant.support) return member

  // Support has no membership to ask about; MainApp answers the shop itself,
  // and only to the support account. Anything else is a refusal.
  if (!supportAccount) return null
  try {
    return supportAccess(await fetchSupportShop(tenant.id))
  } catch (error) {
    if (error instanceof ClientResponseError && (error.status === 404 || error.status === 403)) {
      return null
    }
    throw error
  }
}

/**
 * Whether the tenant session still works. The shard ends every session of a
 * person whose access an admin changes, but PocketBase treats a dead token on
 * an ordinary request as nobody at all — empty lists and refusals, not a 401 —
 * so the dashboard asks directly. A refresh answers 401 for a dead session,
 * and renews a live one.
 */
export function tenantSessionAlive(): Promise<boolean> {
  sessionCheck ??= checkTenantSession().finally(() => {
    sessionCheck = null
  })
  return sessionCheck
}

let sessionCheck: Promise<boolean> | null = null

async function checkTenantSession(): Promise<boolean> {
  if (!tenantPb.authStore.token) return true
  try {
    await tenantPb.collection("users").authRefresh()
    return true
  } catch (error) {
    // Anything but a refusal — offline, a server restarting — says nothing
    // about the session, and is asked again next time.
    if (error instanceof ClientResponseError && error.status === 401) {
      tenantPb.authStore.clear()
      return false
    }
    return true
  }
}

/** Forgets both sessions. Firebase sign-out is the caller's business. */
export function clearSessions(): void {
  mainPb.authStore.clear()
  // Every business's, not only the one this tab is in: signing out ends them
  // all, in every tab.
  tenantAuthStore.clearAll()
}
